import type { Directive } from "vue";

// ENG-20861: in-house replacement for quasar's ClosePopup directive. On click, dispatch a
// bubbling "mclosepopup" DOM event (deferred so the element's own @click handler runs first).
// The nearest closable popup ancestor (MMenu) listens for it and closes. `v-close-popup="false"`
// disables it. Dropdown-button/action-list menus close via their own @click handlers, so the
// event harmlessly bubbles past them.

export const CLOSE_POPUP_EVENT = "mclosepopup";

interface ClosePopupEl extends HTMLElement {
  __mClosePopup?: {
    enabled: boolean;
    handler: () => void;
  };
}

export const closePopup: Directive<ClosePopupEl, boolean | undefined> = {
  mounted(el, binding) {
    const ctx = {
      enabled: binding.value !== false,
      handler() {
        if (!ctx.enabled) return;
        // defer so the element's own click handlers (e.g. @click select) run before the
        // popup unmounts — mirrors quasar's ClosePopup setTimeout.
        setTimeout(() => {
          el.dispatchEvent(new CustomEvent(CLOSE_POPUP_EVENT, { bubbles: true }));
        });
      },
    };
    el.__mClosePopup = ctx;
    el.addEventListener("click", ctx.handler);
  },

  updated(el, binding) {
    if (el.__mClosePopup) el.__mClosePopup.enabled = binding.value !== false;
  },

  beforeUnmount(el) {
    if (el.__mClosePopup) {
      el.removeEventListener("click", el.__mClosePopup.handler);
      delete el.__mClosePopup;
    }
  },
};

export default closePopup;
